import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ppholder } from "constants/assets";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useUIMisc from "hooks/useUIMisc";
import QRCode from "qrcode";
import {
  Calendar,
  Clock,
  MapPin,
  QrCode,
  RefreshCw,
  Ticket,
  TicketX,
} from "lucide-react";
import moment from "moment";
import { LandingHeader } from "pages/components";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";

type Props = {};

interface TicketData {
  id: string;
  type: string;
  price: number;
  code: string;
  mainTicketId: string;
  status: "active" | "used" | "expired";
}

const OfflineTicket = (props: Props) => {
  const { id } = useParams();
  const { showLoading, closeLoading, showToast } = useUIMisc();
  const axiosPrivate = useAxiosPrivate();
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [infoData, setInfoData] = useState<any>(null);
  const [selectedTicket, setSelectedTicket] = useState<TicketData | any>(null);
  const [loading, setLoading] = useState(true);

  const getStatusColor = (status: string) => {
    if (status === "used") {
      return "bg-red-100 text-red-800";
    } else if (status === "active") {
      return "bg-green-100 text-green-800";
    } else {
      return "bg-gray-100 text-gray-800";
    }
  };

  const getTickets = async () => {
    showLoading();
    try {
      const ticketRes = await axiosPrivate.get(`/tickets/offline-ticket/${id}`);
      setLoading(false);
      setInfoData(ticketRes.data.data);
      console.log("====================================");
      console.log(ticketRes.data.data);
      console.log("====================================");
      closeLoading();
    } catch (error: any) {
      closeLoading();
      setTimeout(() => {
        setInfoData(null);
        setLoading(false);
      }, 3000);
    }
  };

  useEffect(() => {
    // Generate QR Code
    if (selectedTicket !== null) {
      const scanInfo = JSON.stringify({
        ticketId: selectedTicket.mainTicketId,
        ticketCode: selectedTicket.code,
      });

      QRCode.toDataURL(scanInfo, { width: 300, margin: 1 })
        .then((url: any) => setQrCodeUrl(url))
        .catch((err: any) => console.error("QR Code generation failed:", err));
    }
  }, [selectedTicket]);

  useEffect(() => {
    if (id !== undefined) {
      getTickets();
    }
  }, [id]);

  return (
    <div className="min-h-screen bg-gray-50">
      {infoData !== null && !loading ? (
        <>
          <div className="bg-white shadow-sm">
            <div className="max-w-4xl mx-auto px-4 py-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <img
                    src={
                      infoData.event.image !== undefined &&
                      infoData.event.image !== null
                        ? infoData.event.image.url
                        : ppholder
                    }
                    alt="Event Poster"
                    width={600}
                    height={400}
                    className="rounded-lg shadow-md w-full"
                  />
                </div>
                <div className="space-y-4">
                  <h1 className="text-3xl font-bold text-gray-900">
                    {infoData.event.name}
                  </h1>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-5 h-5" />
                      <span>
                        {moment(infoData.event.startDate).format(
                          "Do MMM. YYYY"
                        )}

                        {infoData.event.endDate !== undefined
                          ? ` - ${moment(infoData.event.endDate).format(
                              "Do MMM. YYYY"
                            )}`
                          : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-5 h-5" />
                      <span>
                        {infoData.event.startTime} - {infoData.event.endTime}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-5 h-5" />
                      <span>
                        {infoData.event.venue.venue_name},{" "}
                        {infoData.event.venue.city}
                      </span>
                    </div>
                  </div>
                  <div className="pt-4">
                    <Badge variant="outline" className="text-sm">
                      <Ticket className="w-4 h-4 mr-1" />
                      {infoData.ticket.tickets.length} Tickets Purchased
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 py-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Your Tickets
            </h2>
            {infoData.ticket !== null &&
            infoData.ticket.tickets.length !== 0 ? (
              <div className="grid gap-4">
                {infoData.ticket.tickets.map((ticket: any) => (
                  <Card
                    key={ticket.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="bg-blue-100 p-3 rounded-lg">
                            <Ticket className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">
                              {ticket.type}
                            </h3>
                            <Badge className={getStatusColor(ticket.status)}>
                              {ticket.status.charAt(0).toUpperCase() +
                                ticket.status.slice(1)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : null}
          </div>

          <Dialog
            open={!!selectedTicket}
            onOpenChange={() => setSelectedTicket(null)}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Ticket Details</DialogTitle>
              </DialogHeader>
              {selectedTicket && (
                <div className="space-y-6">
                  <div className="flex justify-center">
                    <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                      <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                        <div className="text-center">
                          {qrCodeUrl ? (
                            <>
                              <img
                                src={qrCodeUrl}
                                alt="QR Code"
                                className="w-full h-36 mt-2 object-contain"
                              />
                            </>
                          ) : null}
                          <p className="text-xs text-gray-500 font-mono break-all px-2">
                            {selectedTicket.code}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="text-center">
                      <h3 className="font-bold text-xl">
                        {selectedTicket.type}
                      </h3>
                      <p className="text-gray-600">{infoData.event.name}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold text-gray-700">Ticket ID</p>
                        <p className="text-gray-600">{selectedTicket.code}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700">Status</p>
                        <Badge
                          className={getStatusColor(selectedTicket.status)}
                        >
                          {selectedTicket.status.charAt(0).toUpperCase() +
                            selectedTicket.status.slice(1)}
                        </Badge>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <strong>Date:</strong>{" "}
                          <span>
                            {moment(infoData.event.startDate).format(
                              "Do MMM. YYYY"
                            )}

                            {infoData.event.endDate !== undefined
                              ? ` - ${moment(infoData.event.endDate).format(
                                  "Do MMM. YYYY"
                                )}`
                              : ""}
                          </span>
                        </p>
                        <p>
                          <strong>Time:</strong> {infoData.event.startTime} -{" "}
                          {infoData.event.endTime}
                        </p>
                        <p>
                          <strong>Venue:</strong>{" "}
                          {infoData.event.venue.venue_name},{" "}
                          {infoData.event.venue.city}
                        </p>
                        <p>
                          <strong>Location:</strong>{" "}
                          {infoData.event.venue.country}
                        </p>
                      </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        <strong>Important:</strong> Please present this QR code
                        at the venue entrance for admission.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      ) : infoData === null && !loading ? (
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="w-full max-w-2xl p-6">
            <Card>
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                  <TicketX className="h-8 w-8 text-red-600" />
                </div>
                <CardTitle className="text-xl">Ticket Not Found</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-center">
                <h6>
                  The ticket you're looking for doesn't exist or may have been
                  deleted.
                </h6>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : infoData === null && loading ? (
        <div className="flex items-center justify-center min-h-screen px-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
                <CardTitle>Searching for ticket...</CardTitle>
              </div>
              <CardDescription>
                Please wait while we look up your ticket
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Separator />
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-20" />
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default OfflineTicket;
